import { absoluteUrl, SITE } from "./site";

export const META_DESCRIPTION_MAX = 159;

/** Trim to under 160 characters, breaking at the last word boundary when possible. */
export function trimMetaDescription(description: string, max = META_DESCRIPTION_MAX): string {
  const normalized = description.replace(/\s+/g, " ").trim();
  if (normalized.length <= max) return normalized;
  const truncated = normalized.slice(0, max);
  const lastSpace = truncated.lastIndexOf(" ");
  return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated).trim();
}

export const SCHEMA_AREA_SERVED = [
  { "@type": "AdministrativeArea", name: "Wales" },
  { "@type": "AdministrativeArea", name: "South West England" },
] as const;

export interface PageSeoOptions {
  /** Path on the canonical domain, e.g. "/about". Must start with a slash. */
  path: string;
  title: string;
  description: string;
  /** Absolute or root-relative image URL. Defaults to the site OG image. */
  image?: string;
  /** Override og:type (defaults to "website"). */
  ogType?: string;
}

export interface ServiceJsonLdOptions {
  name: string;
  description: string;
  serviceType: string;
  url: string;
  image?: string;
}

/** Service schema for domestic and commercial service landing pages. */
export function serviceJsonLd(opts: ServiceJsonLdOptions) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: trimMetaDescription(opts.description),
    ...(opts.image && { image: opts.image }),
    serviceType: opts.serviceType,
    provider: {
      "@type": "Organization",
      name: SITE.name,
      "@id": `${SITE.url}/#organization`,
    },
    areaServed: [...SCHEMA_AREA_SERVED],
    url: opts.url,
  };
}

export function serviceJsonLdScript(opts: ServiceJsonLdOptions) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify(serviceJsonLd(opts)),
  };
}

type FaqItem =
  | { question: string; answer: string }
  | { q: string; a: string };

/** FAQPage schema from existing on-page FAQ content. */
export function faqPageJsonLdScript(faqs: FaqItem[]) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: "question" in f ? f.question : f.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: "answer" in f ? f.answer : f.a,
        },
      })),
    }),
  };
}

/** Build a meta + links array suitable for a TanStack Start route head(). */
export function pageSeo({ path, title, description, image, ogType }: PageSeoOptions) {
  const trimmedDescription = trimMetaDescription(description);
  const url = absoluteUrl(path);
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : absoluteUrl(image)
    : `${SITE.url}/og-default.jpg`;

  return {
    meta: [
      { title },
      { name: "description", content: trimmedDescription },
      { property: "og:title", content: title },
      { property: "og:description", content: trimmedDescription },
      { property: "og:url", content: url },
      { property: "og:image", content: ogImage },
      { property: "og:type", content: ogType ?? "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: trimmedDescription },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}
